import { useEffect, useMemo, useState, useContext, useCallback, useRef } from 'react';
import { useForm, Controller } from "react-hook-form";
import { useNavigate, useOutletContext } from "react-router-dom";
import { TextField, Checkbox, ListSubheader, FormControlLabel, Select, MenuItem } from '@mui/material';
import { MdDelete } from "react-icons/md";
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import apiAdmin from '../../../services/api-admin';
import { AdminUserContext } from '../../../contexts/user-context';
import { FormatMoney } from '../../../services/utilities';
import {loadStripe} from '@stripe/stripe-js';

const STORAGE_KEY = 'merchOneCart';
const TAX_RATE = 0.06;

const stripeAppearance = {
  theme: 'stripe',

  variables: {
    colorPrimary: '#000',
    colorBackground: '#ffffff',
    colorText: '#000',
    fontFamily: 'Noto Sans, sans-serif',
  }
};

const StripePaymentElement = ({ onConfirmReady }) => {
    const stripe = useStripe();
    const elements = useElements();

    useEffect(() => {
        onConfirmReady(async () => {
            if (!stripe || !elements) {
                return { error: { message: 'Payment form is still loading. Please try again.' } };
            }

            const result = await stripe.confirmPayment({
                elements,
                redirect: 'if_required'
            });

            if (result.error) {
                return { error: result.error };
            }

            if (result.paymentIntent?.status !== 'succeeded') {
                return { error: { message: 'Payment has not completed yet. Please try again.' } };
            }

            return { paymentIntent: result.paymentIntent };
        });
    }, [elements, onConfirmReady, stripe]);

    return <PaymentElement />;
};

const ShoppingCartCheckout = () => {
    const { bucksOneBalance, setBucksOneBalance, isBucksOneLoading, cartItems, setCartItems, removeFromCart } = useOutletContext();
    const navigate = useNavigate();
    const { adminUserProfile } = useContext(AdminUserContext);
    const { register, handleSubmit, reset, watch, setValue, control, formState: { errors } } = useForm({ defaultValues: { notes: '', couponCode: '' } });
    const [pageError, setPageError] = useState(null);
    const [submitted, setSubmitted] = useState(false);
    const [teamMemberInfo, setTeamMemberInfo] = useState({ teamMemberId: "", directLine: "", extension: "", primaryOfficeId: null, department: "" });
    const [isCartLoading, setIsCartLoading] = useState(true);
    const [useBucksOne, setUseBucksOne] = useState(false);
    const [couponState, setCouponState] = useState({ applied: false, notFound: false, code: null, subtotal: null, discountAmount: 0, discountedSubtotal: null, tax: null, total: null });
    const couponCode = watch("couponCode");
    const [newBucksOneBalance, setNewBucksOneBalance] = useState(null);
    const [regions, setRegions] = useState([]);
    const [checkoutStatus, setCheckoutStatus] = useState(null);
    const stripePromise = useMemo(() => loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY), []);
    const [paymentClientSecret, setPaymentClientSecret] = useState(null);
    const [paymentIntentId, setPaymentIntentId] = useState(null);
    const [confirmStripePayment, setConfirmStripePayment] = useState(null);
    const isCreatingPaymentIntentRef = useRef(false);
    const paymentAmountSnapshotRef = useRef(null);

    useEffect(() => {
        try {
            const stored = window.localStorage.getItem(STORAGE_KEY);
            const parsed = stored ? JSON.parse(stored) : [];
            setCartItems(Array.isArray(parsed) ? parsed : []);
        } catch {
            setCartItems([]);
        } finally {
            setIsCartLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!adminUserProfile) return;

        apiAdmin.get(`/get-team-member-info`)
            .then((response) => {
                const info = response.data ?? {};
                setTeamMemberInfo({
                    teamMemberId: info.teamMemberId ?? "",
                    directLine: info.directLine ?? "",
                    extension: info.extension ?? "",
                    primaryOfficeId: info.primaryOfficeId ?? null,
                    department: info.department ?? ""
                });
            })
            .catch((err) => {
                console.error("Failed to load team member info", err);
            });
    }, [adminUserProfile]);

    useEffect(() => {
        apiAdmin.get(`/regions-with-offices`)
            .then((response) => {
                setRegions(response.data ?? []);
            })
            .catch((err) => console.error("Failed to load regions", err));
    }, []);

    useEffect(() => {
        if (!teamMemberInfo?.primaryOfficeId) return;
        if (!regions?.length) return;

        const allOffices = regions.flatMap(r => r.offices ?? []);
        const primaryOffice = allOffices.find(o => o.officeId === teamMemberInfo.primaryOfficeId);

        if (primaryOffice?.officeName) {
            setValue("officeLocation", primaryOffice.officeName, { shouldValidate: true });
        }
    }, [teamMemberInfo?.primaryOfficeId, regions, setValue]);

    useEffect(() => {
        if (submitted || pageError) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }, [submitted, pageError]);

    const subtotal = useMemo(() => {
        return cartItems.reduce((sum, item) => {
            const base = Number(item.costPerItem ?? 0);
            const up = Number(item.sizeUpCharge ?? 0);
            const qty = Number(item.quantity ?? 0);
            return sum + (base + up) * qty;
        }, 0);
    }, [cartItems]);

    const displaySubtotal = couponState.applied && couponState.subtotal != null ? couponState.subtotal : subtotal;
    const defaultTax = useMemo(() => displaySubtotal * TAX_RATE, [displaySubtotal]);
    const defaultTotal = useMemo(() => displaySubtotal + defaultTax, [displaySubtotal, defaultTax]);
    const tax = (couponState.applied && couponState.tax != null) ? couponState.tax : defaultTax;
    const total = (couponState.applied && couponState.total != null) ? couponState.total : defaultTotal;
    const useBucksOneEffective =
        useBucksOne && !isBucksOneLoading && bucksOneBalance > 0 && total > 0;

    const bucksOneApplied = useMemo(() => {
        if (!useBucksOneEffective) return 0;
        return Math.min(bucksOneBalance, total);
    }, [useBucksOneEffective, bucksOneBalance, total]);

    const amountChargedToCard = useMemo(() => {
        const amt = total - bucksOneApplied;
        return Math.max(0, Math.round(amt * 100) / 100);
    }, [total, bucksOneApplied]);

    const creditCardUsed = amountChargedToCard > 0;
    const bucksOneUsedFlag = bucksOneApplied > 0;
    const onConfirmReady = useCallback((confirmFn) => {
        setConfirmStripePayment(() => confirmFn);
    }, []);

    useEffect(() => {
        if (!creditCardUsed) {
            paymentAmountSnapshotRef.current = null;
            return;
        }

        const nextSnapshot = `${Number(total).toFixed(2)}|${Number(amountChargedToCard).toFixed(2)}`;
        if (paymentAmountSnapshotRef.current == null) {
            paymentAmountSnapshotRef.current = nextSnapshot;
            return;
        }

        if (paymentAmountSnapshotRef.current !== nextSnapshot) {
            isCreatingPaymentIntentRef.current = false;
            setPaymentClientSecret(null);
            setPaymentIntentId(null);
            setConfirmStripePayment(null);
            if (checkoutStatus) {
                setCheckoutStatus(null);
            }
        }

        paymentAmountSnapshotRef.current = nextSnapshot;
    }, [creditCardUsed, total, amountChargedToCard, checkoutStatus]);

    useEffect(() => {
        if (!creditCardUsed) {
            isCreatingPaymentIntentRef.current = false;
            setPaymentClientSecret(null);
            setPaymentIntentId(null);
            setConfirmStripePayment(null);
            if (checkoutStatus === 'awaiting-payment') {
                setCheckoutStatus(null);
            }
            return;
        }

        if (paymentClientSecret || paymentIntentId || isCreatingPaymentIntentRef.current) {
            return;
        }

        if (!teamMemberInfo.teamMemberId || cartItems.length === 0) {
            return;
        }

        isCreatingPaymentIntentRef.current = true;
        setCheckoutStatus('initializing');

        apiAdmin.post(`/create-payment-intent`, {
            teamMemberId: teamMemberInfo.teamMemberId,
            email: adminUserProfile?.email ?? "",
            couponUsed: couponState.applied,
            couponCode: couponState.applied ? couponState.code : null,
            bucksOneUsed: bucksOneUsedFlag,
            items: cartItems.map(i => ({
                merchOneProductId: i.merchOneProductId,
                productNumber: i.productNumber,
                quantity: i.quantity,
                size: i.size,
                description: i.description,
                costPerItem: i.costPerItem,
                sizeUpCharge: Number(i.sizeUpCharge ?? 0),
                color: i.color,
                companyLogo: i.companyLogo,
                logoLocationId: i.logoLocationId,
                inHouseFulfillment: i.inHouseFulfillment
            }))
        })
            .then(({ data }) => {
                if (!data?.success || !data?.clientSecret || !data?.paymentIntentId) {
                    throw new Error(data?.error || 'Unable to initialize Stripe payment.');
                }

                setPaymentClientSecret(data.clientSecret);
                setPaymentIntentId(data.paymentIntentId);
                setCheckoutStatus('awaiting-payment');
            })
            .catch((err) => {
                console.error('Create payment intent failed', err);
                setCheckoutStatus(null);
                setPageError('Unable to initialize payment right now. Please try again.');
            })
            .finally(() => {
                isCreatingPaymentIntentRef.current = false;
            });
    }, [
        creditCardUsed,
        amountChargedToCard,
        paymentClientSecret,
        paymentIntentId,
        checkoutStatus,
        teamMemberInfo.teamMemberId,
        cartItems,
        adminUserProfile?.email,
        couponState.applied,
        couponState.code,
        bucksOneUsedFlag
    ]);

    const applyCoupon = useCallback(async () => {
        setPageError(null);

        const code = (couponCode || '').trim();
        if (!code) {
            setCouponState({
                applied: false,
                notFound: false,
                code: null,
                subtotal: null,
                discountAmount: 0,
                discountedSubtotal: null,
                tax: null,
                total: null
            });
            return;
        }

        try {
            const response = await apiAdmin.post('/validate-merchone-coupon', {
                teamMemberId: teamMemberInfo.teamMemberId, 
                couponCode: code,
                items: cartItems.map(i => ({
                    productNumber: i.productNumber,
                    quantity: i.quantity,
                    size: i.size,
                    sizeUpCharge: Number(i.sizeUpCharge ?? 0)
                }))
            });

            const data = response.data;

            if (!data?.valid) {
                setCouponState({
                    applied: false,
                    notFound: true,
                    code: null,
                    subtotal: null, 
                    discountAmount: 0,
                    discountedSubtotal: null,
                    tax: null,
                    total: null
                });
                return;
            }

            setCouponState({
                applied: true,
                notFound: false,
                code: data.couponCode ?? code,
                subtotal: data.subtotal != null ? Number(data.subtotal) : null, 
                discountAmount: Number(data.discountAmount ?? 0),
                discountedSubtotal: data.discountedSubtotal != null ? Number(data.discountedSubtotal) : null,
                tax: data.tax != null ? Number(data.tax) : null,
                total: data.total != null ? Number(data.total) : null
            });

        } catch (err) {
            console.error('Coupon validation failed', err);
            setPageError('Unable to validate coupon code right now. Please try again.');
        }
    }, [couponCode, cartItems, teamMemberInfo.teamMemberId]);

    const submitOrder = useCallback(async (requestObject) => {
        const { data } = await apiAdmin.post(`/save-merchone-order`, requestObject);

        if (!data?.success) {
            throw new Error(data?.error || "There was an error saving the order.");
        }

        window.localStorage.removeItem(STORAGE_KEY);
        setCheckoutStatus(null);
        setCartItems([]);
        reset();
        setValue('couponCode', '');
        setPaymentClientSecret(null);
        setPaymentIntentId(null);
        setConfirmStripePayment(null);
        setCouponState({
            applied: false,
            notFound: false,
            code: null,
            subtotal: null,
            discountAmount: 0,
            discountedSubtotal: null,
            tax: null,
            total: null
        });

        setSubmitted(true);
        if (data.newBucksOneBalance != null) {
            setBucksOneBalance(Number(data.newBucksOneBalance));
            setNewBucksOneBalance(Number(data.newBucksOneBalance));
        } else {
            setNewBucksOneBalance(null);
        }
    }, [reset, setBucksOneBalance, setCartItems, setValue]);

    const onSubmit = async (formData) => {
        setPageError(null);
        setSubmitted(false);

        if (cartItems.length === 0) {
            setPageError("Your cart is empty.");
            return;
        }
        const requestObject = {
            name: adminUserProfile?.displayName ?? "",
            email: adminUserProfile?.email ?? "",
            phone: teamMemberInfo.extension
                ? `${teamMemberInfo.directLine} x${teamMemberInfo.extension}`
                : teamMemberInfo.directLine,
            officeLocation: formData.officeLocation,
            department: teamMemberInfo.department,
            teamMemberId: teamMemberInfo.teamMemberId,
            notes: formData.notes ?? "",
            couponUsed: couponState.applied,
            couponCode: couponState.applied ? couponState.code : null,
            bucksOneUsed: bucksOneUsedFlag,
            paymentIntentId: null,

            items: cartItems.map(i => ({
                merchOneProductId: i.merchOneProductId,
                productNumber: i.productNumber,
                quantity: i.quantity,
                size: i.size, 
                description: i.description,
                costPerItem: i.costPerItem,
                sizeUpCharge: Number(i.sizeUpCharge ?? 0),
                color: i.color,
                companyLogo: i.companyLogo,
                logoLocationId: i.logoLocationId,
                inHouseFulfillment: i.inHouseFulfillment
            }))
        };

        try {
            if (creditCardUsed) {
                if (!confirmStripePayment) {
                    throw new Error('Payment form is still loading. Please try again.');
                }

                setCheckoutStatus('processing-payment');
                const paymentResult = await confirmStripePayment();
                if (paymentResult?.error) {
                    throw new Error(paymentResult.error.message || 'Stripe payment failed.');
                }

                const confirmedPaymentIntentId = paymentResult?.paymentIntent?.id ?? paymentIntentId;
                if (!confirmedPaymentIntentId) {
                    throw new Error('Missing Stripe payment intent id after confirmation.');
                }

                await submitOrder({
                    ...requestObject,
                    paymentIntentId: confirmedPaymentIntentId
                });
                return;
            }

            await submitOrder(requestObject);
        } catch (err) {
            console.error("Checkout submit failed", err);
            setCheckoutStatus(null);
            setPageError("There was an error saving the order. Please try again.");
        }
    };

    return (
        <div className="container py-3">
            <title>MerchOne Checkout</title>
            {pageError && <div className="alert alert-danger" role="alert">{pageError}</div>}
            {submitted ? (
                <div className="alert alert-success" role="alert">
                    Your order has been successfully submitted and you will receive a confirmation email.
                    {newBucksOneBalance != null && (
                        <> Your BucksOne balance is ${Number(newBucksOneBalance).toFixed(2)}.</>
                    )}
                </div>
            ) : isCartLoading ? (
                <div className="text-secondary">Loading cart…</div>
            ) : cartItems.length === 0 ? (
                <div className="alert alert-warning">
                        Your cart is empty.{" "}
                    <button
                        className="btn btn-link p-0"
                        type="button"
                        onClick={() => navigate("/merchone")}
                    >
                        Return to the store
                    </button>
                    .
                </div>
            ) : (
                <div className="row g-3">
                    <div className="col-12 col-lg-7 order-2 order-lg-1">
                        <form onSubmit={handleSubmit(onSubmit)}>
                            <div className="border rounded p-3">
                                <div className="mb-2">
                                    <strong>Name:</strong> {adminUserProfile?.displayName ?? "—"}
                                </div>
                                <div className="mb-2">
                                    <strong>Email:</strong> {adminUserProfile?.email ?? "—"}
                                </div>
                                <div className="mb-2">
                                    <strong>Direct Line:</strong> {teamMemberInfo.directLine || "—"}{" "}
                                    {teamMemberInfo.extension ? (
                                        <span><strong>Ext:</strong> {teamMemberInfo.extension}</span>
                                    ) : null}
                                </div>
                                <div className="mb-2">
                                    <strong>Shipping Office: </strong>
                                    <Controller
                                        name="officeLocation"
                                        control={control}
                                        defaultValue=""
                                        rules={{ required: "Office is required" }}
                                        render={({ field }) => (
                                            <Select
                                                labelId="office-label"
                                                id="office"
                                                value={field.value ?? ""}
                                                onChange={(e) => field.onChange(e.target.value)} 
                                                displayEmpty
                                            >
                                                <MenuItem value="" disabled>
                                                    Select an office
                                                </MenuItem>
                                                {regions
                                                    .filter(r => r.offices?.length)
                                                    .flatMap(region => [
                                                        <ListSubheader
                                                            key={`${region.regionId ?? region.regionName}-hdr`}
                                                            disableSticky
                                                        >
                                                            {region.regionName}
                                                        </ListSubheader>,
                                                        ...(region.offices ?? []).map(o => (
                                                            <MenuItem key={o.officeId} value={o.officeName}> 
                                                                {o.officeName}
                                                            </MenuItem>
                                                        ))
                                                    ])}
                                            </Select>
                                        )}
                                    />
                                </div>
                                <div className="mb-3">
                                    <TextField
                                        label="Notes"
                                        {...register("notes")}
                                        className="w-100"
                                        multiline
                                        minRows={3}
                                    />
                                </div>
                                <div className="row g-2 align-items-start mb-2">
                                    <div className="col-12 col-md-7">
                                        <TextField
                                            label="Coupon Code"
                                            {...register("couponCode")}
                                            className="w-100"
                                            error={Boolean(errors.couponCode)}
                                            helperText={errors.couponCode?.message}
                                        />
                                        {couponState.applied && (
                                            <small className="text-success">
                                                Coupon applied (−{FormatMoney(couponState.discountAmount)}).
                                            </small>
                                        )}
                                        {couponState.notFound && (
                                            <small className="text-danger">Coupon not found / not valid.</small>
                                        )}
                                    </div>
                                    <div className="col-12 col-md-5">
                                                <button type="button" className="btn btn-primary w-100 " onClick={applyCoupon}>
                                            Apply Coupon
                                        </button>
                                    </div>
                                </div>
                                <div className="mt-3">
                                    <div className="text-secondary d-flex flex-column flex-md-row align-items-start align-items-md-center gap-2">
                                        <span>BucksOne Balance:</span>
                                        {isBucksOneLoading ? (
                                            <span className="placeholder bg-light col-4 d-inline-block" />
                                        ) : (
                                            <span className="col-4">{FormatMoney(bucksOneBalance)}</span>
                                        )}
                                        <div className="ms-md-auto">
                                            <FormControlLabel
                                                className="m-0"
                                                control={
                                                    <Checkbox
                                                        checked={useBucksOne}
                                                        onChange={(e) => setUseBucksOne(e.target.checked)}
                                                        disabled={isBucksOneLoading || bucksOneBalance <= 0 || total <= 0}
                                                    />
                                                }
                                                label="Use BucksOne"
                                            />
                                        </div>
                                    </div>

                                    <div className="border rounded p-3 mt-2">
                                        <h6 className="mb-2">Payment Breakdown</h6>
                                        <div className="d-flex justify-content-between">
                                            <div>Total</div>
                                            <div className="fw-semibold">{FormatMoney(total)}</div>
                                        </div>
                                        <div className="d-flex justify-content-between">
                                            <div>BucksOne Applied</div>
                                            <div className={bucksOneApplied > 0 ? "text-success fw-semibold" : "text-secondary"}>
                                                -{FormatMoney(bucksOneApplied)}
                                            </div>
                                        </div>
                                        <div className="d-flex justify-content-between">
                                            <div>Amount Charged to Card</div>
                                            <div className="fw-semibold">{FormatMoney(amountChargedToCard)}</div>
                                        </div>
                                    </div>
                                </div>
                                {creditCardUsed && paymentClientSecret && (
                                    <div className="mt-3">
                                        <Elements stripe={stripePromise} options={{ clientSecret: paymentClientSecret, appearance: stripeAppearance  }}>
                                            <StripePaymentElement onConfirmReady={onConfirmReady} />
                                        </Elements>
                                    </div>
                                )}
                                <div className="mt-3">
                                    {!creditCardUsed ? (
                                        <button
                                            type="submit"
                                            className="btn btn-primary w-100"
                                            aria-label="Submit Order"
                                            disabled={cartItems.length === 0 || checkoutStatus === 'initializing' || checkoutStatus === 'processing-payment'}
                                        >
                                            Submit Order
                                    </button>
                                    ) : (
                                        <button
                                            type="submit"
                                            className="btn btn-primary w-100"
                                            aria-label="Pay and Submit Order"
                                            disabled={cartItems.length === 0 || checkoutStatus === 'initializing' || checkoutStatus === 'processing-payment' || !paymentClientSecret || !confirmStripePayment}
                                        >
                                            {checkoutStatus === 'processing-payment'
                                                ? 'Processing Payment...'
                                                : checkoutStatus === 'initializing'
                                                    ? 'Preparing Payment...'
                                                    : 'Pay and Submit Order'}
                                        </button>
                                    )}
                                    
                                </div>
                            </div>
                        </form>
                    </div>
                        <div className="col-12 col-lg-5 order-1 order-lg-2">
                        <div className="border rounded p-3">
                            <h5 className="mb-3">Order Summary</h5>
                            <div className="d-flex flex-column gap-3">
                                {cartItems.map((item) => {
                                    const name = item.description ?? 'Item';
                                    const base = Number(item.costPerItem ?? 0);
                                    const up = Number(item.sizeUpCharge ?? 0);
                                    const unitPrice = base + up;
                                    const qty = Number(item.quantity ?? 0);
                                    const size = item.size ?? 'One Size';
                                    const color = item.color;
                                    const logo = item.companyLogo;

                                    return (
                                        <div
                                            key={`${item.productNumber ?? item.merchOneProductId}-${size}-${color ?? ''}-${logo ?? ''}`}
                                            className="d-flex align-items-center justify-content-between gap-3"
                                        >
                                            <div className="d-flex align-items-center gap-3">
                                                <div className="d-flex align-items-center justify-content-center flex-shrink-0 bg-white border rounded cart-image">
                                                    <img src={item.imageUrl} alt={name} className="mw-100 mh-100 object-fit-contain p-1" loading="lazy" />
                                                </div>
                                                <div>
                                                    <div className="fw-semibold">{name}</div>
                                                    <div className="text-secondary small">
                                                        {FormatMoney(base)}
                                                        {up > 0 ? ` + ${FormatMoney(up)} (size)` : ""} · Qty {qty} · {size}
                                                    </div>
                                                    {(logo || color) && (
                                                        <div className="text-secondary small">
                                                            {logo ? `Logo: ${logo}` : null}
                                                            {logo && color ? ' · ' : null}
                                                            {color ? `Color: ${color}` : null}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            <button type="button" onClick={() => removeFromCart(item)} className="btn btn-link p-0 ms-auto" aria-label="Remove from cart">
                                                <MdDelete />
                                            </button>
                                            <div className="fw-semibold text-nowrap">
                                                {FormatMoney(unitPrice * qty)}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                            <hr />
                            <div className="d-flex justify-content-between">
                            <div>Subtotal</div>
                                <div>{FormatMoney(displaySubtotal)}</div>
                            </div>
                                {couponState.applied && (
                                    <div className="d-flex justify-content-between text-success">
                                        <div>Coupon Discount</div>
                                        <div>-{FormatMoney(couponState.discountAmount)}</div>
                                    </div>
                                )}
                            <div className="d-flex justify-content-between">
                                <div>Tax</div>
                                <div>{FormatMoney(tax)}</div>
                            </div>
                            <div className="d-flex justify-content-between fw-bold fs-5 mt-2">
                                <div>Total</div>
                                <div>{FormatMoney(total)}</div>
                            </div>
                        </div>
                     </div>
                </div>
            )}
        </div>
    );
};

export default ShoppingCartCheckout;